import { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct, UnitOfMeasureConversion } from "@msdyn365-commerce/retail-proxy";
import { humanizeUnitOfMeasure } from "../components/unit-of-measure";

export function getMostRelevantConversion(conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    // Accepted units, sorted by priority
    const acceptedToUnits = [
        'stk',
        'Stykk',
        'Stk-BS',
        'Kilogram',
        'kg',
        'Liter',
        '100_meter'
    ];

    for (const unit of acceptedToUnits) {
        const find = conversions?.find(conversion => conversion.ToUnitOfMeasureSymbol === unit);
        if (find) {
            return find;
        }
    }

    return undefined;
}

export function getKilogramConversion(defaultUnitOfMeasure: string | undefined, conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    if (!defaultUnitOfMeasure) {
        return undefined;
    }

    const acceptedToUnits = [
        'Kilogram',
        'kg'
    ];
    return conversions?.find(conversion =>
        conversion.FromUnitOfMeasureSymbol === defaultUnitOfMeasure &&
        conversion.ToUnitOfMeasureSymbol && acceptedToUnits.includes(conversion.ToUnitOfMeasureSymbol));
}

export function getLiterConversion(defaultUnitOfMeasure: string | undefined, conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    if (!defaultUnitOfMeasure) {
        return undefined;
    }

    const acceptedToUnits = [
        'Liter'
    ];
    return conversions?.find(conversion =>
        conversion.FromUnitOfMeasureSymbol === defaultUnitOfMeasure &&
        conversion.ToUnitOfMeasureSymbol && acceptedToUnits.includes(conversion.ToUnitOfMeasureSymbol));
}

export function getPcsConversion(conversions: UnitOfMeasureConversion[], fromUOM?: string): UnitOfMeasureConversion | undefined {
    const acceptedFromUnits = fromUOM ? [fromUOM] : [
        'Krt-M1',
        'Krt-BS',
        'Brett-M1',
        'Stk-M1'
    ];
    const acceptedToUnits = [
        'stk',
        'Stykk',
        'Stk-BS',
        'Pose-BS',
        'Flaske-BS',
        'Boks-BS',
        'Kanne-BS'
    ];

    return conversions?.find(conversion =>
        (conversion.FromUnitOfMeasureSymbol && acceptedFromUnits.includes(conversion.FromUnitOfMeasureSymbol)) &&
        (conversion.ToUnitOfMeasureSymbol && acceptedToUnits.includes(conversion.ToUnitOfMeasureSymbol)));
}

export function getKilogramForKilogramProduct(conversions: UnitOfMeasureConversion[]): UnitOfMeasureConversion | undefined {
    const acceptedFromUnits = [
        'Kilogram',
        'kg'
    ];

    return conversions?.find(conversion => {
        return conversion.FromUnitOfMeasureSymbol?.endsWith('-BS') && acceptedFromUnits.includes(conversion.ToUnitOfMeasureSymbol || '');
    });
}

export function getConversionText(
    contextOrLocale: ICoreContext | string,
    uom: string | undefined,
    conversions: UnitOfMeasureConversion[],
    simpleProduct: SimpleProduct | undefined,
    breakoff: boolean
): string | undefined {
    // Get conversions for pcs, liter, and kilogram
    const pcs = getPcsConversion(conversions, uom);
    const liter = getLiterConversion(uom, conversions);
    const kilogram = getKilogramConversion(uom, conversions);

    let result: string | undefined;

    // Special case: product has default UoM 'kg'
    if (simpleProduct?.DefaultUnitOfMeasure === 'kg') {
        const kg = getKilogramForKilogramProduct(conversions);
        if (kg) {
            return `ca ${kg.Factor || 0}kg per stk`;
        } else if (!breakoff && simpleProduct.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) {
            return `ca ${simpleProduct.Behavior.MultipleOfQuantity} kg`;
        }
    }

    // Override result if liter conversion exists
    if (liter) {
        const literPer = normalizeLiter((liter.Factor || 0) / (pcs?.Factor || 1));
        result = pcs
            ? `${pcs.Factor} x ${literPer}`
            : `${liter.Factor}l`;
    } else if (kilogram?.Factor) {
        result = pcs
            ? `${pcs.Factor} x ${normalizeKilogram(kilogram.Factor / (pcs.Factor || 1))}`
            : `${normalizeKilogram(kilogram.Factor)}`;
    } else if (pcs) {
        result = `${pcs.Factor} ${humanizeUnitOfMeasure(contextOrLocale, pcs.ToUnitOfMeasureSymbol)}`;
    }

    // Add total amounts of kilogram or liter
    if (result && !breakoff && simpleProduct?.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) {
        // Total amount for breakoff products
        result = `${simpleProduct.Behavior.MultipleOfQuantity} x ${result}`;

        if (liter?.Factor) {
            const totalLiter = Math.round((((liter.Factor || 0) * simpleProduct.Behavior.MultipleOfQuantity) + Number.EPSILON) * 100) / 100;
            result = `${result}, ${totalLiter}l`;
        } else if (kilogram?.Factor) {
            result += `, ${normalizeKilogram(kilogram.Factor * simpleProduct.Behavior.MultipleOfQuantity)}`;
        }
    } else {
        // Total amount for non breakoff products
        if (liter?.Factor && pcs?.Factor) {
            result += `, ${liter.Factor}l`;
        } else if (kilogram?.Factor && pcs?.Factor) {
            result += `, ${normalizeKilogram(kilogram.Factor)}`;
        }
    }

    // Fallback for special case UoMs ending with '-BS'
    if (!result && uom?.endsWith('-BS')) {
        const conv = conversions.find(x =>
            x.ItemId === simpleProduct?.ItemId &&
            x.ToUnitOfMeasureSymbol === uom &&
            x.FromUnitOfMeasureSymbol?.endsWith('-M1')
        );
        if (conv) {
            result = `${conv.Factor} per ${humanizeUnitOfMeasure(contextOrLocale, conv.FromUnitOfMeasureSymbol).toLocaleLowerCase()}`;
        }
    }

    return result;
}

function normalizeLiter(liter: number): string {
    // Round to 2 decimals
    liter = Math.round((liter + Number.EPSILON) * 100) / 100;
    return `${liter}l`;
}

function normalizeKilogram(kilogram: number): string {
    let suffix = 'kg';
    if (kilogram < 1 && kilogram > 0) {
        // Show as grams if less than 1kg
        kilogram = kilogram * 1000;
        suffix = 'g';
    }
    // Round of factor to 2 decimals
    kilogram = Math.round((kilogram + Number.EPSILON) * 100) / 100;
    return `${kilogram}${suffix}`;
}

export function getNonBreakoffUOM(product: SimpleProduct | undefined, unitConversions: UnitOfMeasureConversion[] | undefined): string | undefined {
    if (!product || !unitConversions) {
        return undefined;
    }

    if (product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1 && product.DefaultUnitOfMeasure?.endsWith('-BS')) {
        const conversion = unitConversions.find(x =>
            x.ItemId === product.ItemId
            && x.ToUnitOfMeasureSymbol === product.DefaultUnitOfMeasure
            && x.FromUnitOfMeasureSymbol?.endsWith('-M1'));
        if (conversion) {
            return conversion.FromUnitOfMeasureSymbol;
        }
    }

    if (product.DefaultUnitOfMeasure === 'kg') {
        if (product.Behavior?.MultipleOfQuantity) {
            if (product.Behavior.MultipleOfQuantity > 0 && product.Behavior.MultipleOfQuantity < 1) {
                return 'Stk';
            } else {
                return 'Krt';
            }
        }
    }

    const liter = getLiterConversion(product.DefaultUnitOfMeasure, unitConversions);
    const kilogram = getKilogramConversion(product.DefaultUnitOfMeasure, unitConversions);

    if (!liter && !kilogram && product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1) {
        return 'Stk';
    }

    return undefined;
}

export function getRoundedFactor(conversion: UnitOfMeasureConversion): number {
    // Only round if the conversion is kg/Kilogram
    if (conversion.Factor && (conversion.ToUnitOfMeasureSymbol === 'kg' || conversion.ToUnitOfMeasureSymbol === 'Kilogram')) {
        return Math.round((conversion.Factor + Number.EPSILON) * 100) / 100;
    } else {
        return conversion.Factor || 0;
    }
}

export function getMinimumDecimals(unitOfMeasure: string | undefined): number {
    const units = [
        'kg',
        'Kilogram'
    ];
    return units.includes(unitOfMeasure || '') ? 2 : 0;
}

export function shouldShowBreakoff(product: SimpleProduct | undefined, unitConversions: UnitOfMeasureConversion[] | undefined): boolean {
    if (!product || !unitConversions) {
        return false;
    }

    const conversion = unitConversions.find(x =>
        x.ItemId === product.ItemId
        && x.ToUnitOfMeasureSymbol === product.DefaultUnitOfMeasure
        && x.FromUnitOfMeasureSymbol?.endsWith('-BS'));

    return !conversion;
}

export function getTotalConversionText(context: ICoreContext, product: SimpleProduct | undefined, unitConversions: UnitOfMeasureConversion[] | undefined, quantity: number, breakoff: boolean): string | undefined {
    if (!product || !unitConversions) {
        return undefined;
    }

    const liter = getLiterConversion(product.DefaultUnitOfMeasure, unitConversions);
    const kilogram = getKilogramConversion(product.DefaultUnitOfMeasure, unitConversions);

    if (liter?.Factor) {
        const totalLiter = Math.round((liter.Factor * quantity + Number.EPSILON) * 100) / 100;

        if (breakoff) {
            return `${totalLiter} l`;
        } else {
            let adjustedQuantity = quantity;
            let uom = humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure);

            if (product.DefaultUnitOfMeasure?.endsWith('-BS') && !product.Behavior?.MultipleOfQuantity) {
                return `${totalLiter} l`;
            }

            if (product.DefaultUnitOfMeasure?.endsWith('-M1')) {
                const pcsConversion = getPcsConversion(unitConversions, product.DefaultUnitOfMeasure);
                if (pcsConversion) {
                    adjustedQuantity = (pcsConversion.Factor || 1) * quantity;
                    uom = humanizeUnitOfMeasure(context, pcsConversion.ToUnitOfMeasureSymbol);
                }
            }

            return `${adjustedQuantity} ${uom}\n${totalLiter} l`;
        }
    }

    if (kilogram?.Factor) {
        let factor = kilogram.Factor * quantity;
        let factorSuffix = 'kg';

        if (factor < 1 && factor > 0) {
            // Show as grams if less than 1kg
            factor = Math.round(factor * 1000);
            factorSuffix = 'g';
        }

        // Round of factor to 2 decimals
        factor = Math.round((factor + Number.EPSILON) * 100) / 100;

        if (breakoff) {
            return `${factor} ${factorSuffix}`;
        } else {
            let adjustedQuantity = quantity;
            let uom = humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure);

            if (product.DefaultUnitOfMeasure?.endsWith('-BS') && !product.Behavior?.MultipleOfQuantity) {
                return `${factor} ${factorSuffix}`;
            }

            if (product.DefaultUnitOfMeasure?.endsWith('-M1')) {
                const pcsConversion = getPcsConversion(unitConversions, product.DefaultUnitOfMeasure);
                if (pcsConversion) {
                    adjustedQuantity = (pcsConversion.Factor || 1) * quantity;
                    uom = humanizeUnitOfMeasure(context, pcsConversion.ToUnitOfMeasureSymbol);
                }
            }

            return `${adjustedQuantity} ${uom}\n${factor} ${factorSuffix}`;
        }
    }

    if (product.DefaultUnitOfMeasure === 'kg') {
        if (product.Behavior?.MultipleOfQuantity) {
            return `ca ${quantity} kg`;
        }
    }

    if (!breakoff && product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1) {
        return `${quantity} ${humanizeUnitOfMeasure(context, product.DefaultUnitOfMeasure)}`;
    }

    return undefined;
}
